/* eslint-disable react/require-default-props */
/* eslint-disable react/prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Flex, Box, Image } from 'rebass';
import { Trans } from '@lingui/macro';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { Typography, Button, Avatar, Divider, DownloadFiles } from '@oca/ui';
import { Screen, CommentsList, CommentForm, Account } from '@features/common';
import { SkeletonLoader } from '@lib/skeleton-loader';
import {
  Pencil,
  ClockDash,
  AddUser,
  LocationBold,
  CalendarBold,
  Work,
  DefaultProject,
  DefaultUser,
} from '@oca/icons';
import { getFullName } from '@lib/help-fns';
import noDataImage from '@lib/illustrations/no-data-found.svg';

import UserBox from '@oca/ui/molecules/user-box';
import {
  addMeetingCommentRequest,
  $commentFetching,
} from '../model/add-meeting-comment-flow';

/**
 * MeetingView — updated to:
 * 1) render title at the top
 * 2) move description under title
 * 3) render vertical descriptor list under description (Date, Status, Location, Creator)
 */

export function MeetingView({
  agenda,
  comments,
  creator,
  description,
  endTime,
  id,
  invitedEmployees,
  isLoading,
  location,
  path,
  project,
  startTime,
  status,
  title,
}) {
  return (
    <PageWrapper>
      <Account
        render={({ user }) => (
          <InnerCard>
            <Header
              editPath={`/meetings/${id}/edit`}
              title={title}
              description={description}
              members={invitedEmployees}
              comments={comments}
              isLoading={isLoading}
              status={status}
              creator={creator}
              location={location}
              project={project}
              startTime={startTime}
              canEdit={creator && creator.id === user.id}
            />
            <Divider />
            <Content
              id={id}
              agenda={agenda}
              comments={comments}
              description={description}
              isLoading={isLoading}
            />
          </InnerCard>
        )}
      />
    </PageWrapper>
  );
}

MeetingView.propTypes = {
  title: PropTypes.node,
  invitedEmployees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
    }),
  ),
  agenda: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
    }),
  ),
  comments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
    }),
  ),
  creator: PropTypes.shape({
    avatar: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
  description: PropTypes.string,
  endTime: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLoading: PropTypes.bool,
  location: PropTypes.string,
  path: PropTypes.string,
  project: PropTypes.shape({
    id: PropTypes.number,
    avatar: PropTypes.string,
    name: PropTypes.string,
  }),
  startTime: PropTypes.string,
  status: PropTypes.oneOf(['upcoming', 'cancelled', 'completed']),
};

MeetingView.defaultProps = { invitedEmployees: [], agenda: [], comments: [] };

/** **************** HEADER (title + description + vertical meta list) ************** */
function Header({
  canEdit,
  isLoading,
  title,
  description,
  members = [],
  comments = [],
  editPath,
  status,
  creator,
  location,
  project,
  startTime,
}) {
  return (
    <HeaderRow>
      <HeaderContent>
        <Preloader
          isLoading={isLoading}
          width={400}
          height={40}
          render={() => (
            <>
              <Flex
                justifyContent="space-between"
                alignItems="flex-start"
                mb={1}
              >
                <Box>
                  <Typography fontSize={32} marginBottom={2}>
                    {title}
                  </Typography>
                </Box>

                {canEdit && (
                  <Box>
                    <Button as={Link} to={editPath} variant="secondary">
                      <Pencil />
                    </Button>
                  </Box>
                )}
              </Flex>

              <Box mb={3}>
                <Preloader
                  isLoading={isLoading}
                  width={1}
                  height={48}
                  render={() => (
                    <Typography
                      color="textSecondary"
                      style={{ whiteSpace: 'pre-wrap', marginBottom: 30 }}
                    >
                      {description || <Trans>No description</Trans>}
                    </Typography>
                  )}
                />
              </Box>

              <MetaItem>
                <Descriptor
                  icon={<ClockDash />}
                  field={<Trans>Status</Trans>}
                  render={() =>
                    status ? (
                      <Typography
                        fontWeight={500}
                        color="textSecondary"
                        lineHeight={1}
                      >
                        {status.substr(0, 1).toUpperCase()}
                        {status.substr(1).toLowerCase()}
                      </Typography>
                    ) : (
                      <Typography lineHeight={1}>-</Typography>
                    )
                  }
                />
              </MetaItem>

              <MetaList>
                <MetaItem>
                  <Descriptor
                    icon={<CalendarBold />}
                    field={<Trans>Date & Time</Trans>}
                    render={() => (
                      <Typography
                        fontWeight={500}
                        fontSize={14}
                        color="textSecondary"
                        lineHeight={1}
                      >
                        {startTime ? (
                          <Screen>
                            {({ xsmall }) =>
                              `${dayjs(new Date(startTime)).format(
                                xsmall ? 'dddd LL' : 'ddd ll',
                              )}; ${dayjs(new Date(startTime)).format('LT')}`
                            }
                          </Screen>
                        ) : (
                          '---'
                        )}
                      </Typography>
                    )}
                  />
                </MetaItem>

                <MetaItem>
                  <Descriptor
                    icon={<Work size={20} />}
                    field={<Trans>Project</Trans>}
                    render={() => (
                      <UserBox mb="-6px">
                        <Avatar
                          src={project ? project.avatar : undefined}
                          size={24}
                          mr={2}
                        >
                          <DefaultProject />
                        </Avatar>
                        <Typography noWrap>
                          {project ? project.name : '---'}
                        </Typography>
                      </UserBox>
                    )}
                  />
                </MetaItem>

                <MetaItem>
                  <Descriptor
                    icon={<LocationBold />}
                    field={<Trans>Location</Trans>}
                    render={() => (
                      <Typography
                        fontWeight={500}
                        color="textSecondary"
                        lineHeight={1}
                      >
                        {location || '---'}
                      </Typography>
                    )}
                  />
                </MetaItem>
                <MetaItem>
                  <Descriptor
                    icon={<AddUser size={20} />}
                    field={<Trans>Creator</Trans>}
                    render={() => (
                      <UserBox mb="-6px">
                        <Avatar
                          src={creator ? creator.avatar : undefined}
                          size={24}
                          mr={2}
                        >
                          <DefaultUser />
                        </Avatar>
                        <Typography
                          fontWeight={500}
                          color="textSecondary"
                          noWrap
                        >
                          {creator ? getFullName(creator) : '---'}
                        </Typography>
                      </UserBox>
                    )}
                  />
                </MetaItem>
              </MetaList>

              {/* small separator before main content */}
              <Box backgroundColor="stroke" mt={3}>
                <Divider />
              </Box>
            </>
          )}
        />
      </HeaderContent>
    </HeaderRow>
  );
}

/** ***************** CONTENT SECTION (description moved; this contains comments + agenda) *********************** */
function Content({ agenda, description, id, comments, isLoading }) {
  return (
    <Flex py={20} justifyContent="space-between">
      <Box width={[1, 0.66]} mr={3}>
        <CardSection>
          <SectionHeader>
            <Typography variant="h3">
              <Trans>Comments</Trans>
            </Typography>
          </SectionHeader>

          <Box mb={3}>
            <Preloader
              width="100%"
              height={40}
              isLoading={isLoading}
              render={() => (
                <CommentForm
                  id={id}
                  onSubmit={addMeetingCommentRequest}
                  submitStatus={$commentFetching}
                />
              )}
            />
          </Box>

          <CommentsList comments={comments} />
        </CardSection>
      </Box>

      <Box width={[1, 0.32]}>
        <CardSection>
          <SectionHeader>
            <Flex
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <Typography variant="h3">
                <Trans>Agenda</Trans>
              </Typography>
            </Flex>
          </SectionHeader>

          <Preloader
            width={150}
            height={69}
            isLoading={isLoading}
            render={() =>
              agenda && agenda.length > 0 ? (
                <DownloadFiles files={agenda} />
              ) : (
                <Flex alignItems="center" flexDirection="column" py={4}>
                  <Image width={150} src={noDataImage} />
                  <Typography mt={3}>
                    <Trans>No agenda attached</Trans>
                  </Typography>
                </Flex>
              )
            }
          />
        </CardSection>
      </Box>
    </Flex>
  );
}

/** Re-used helpers */
function Preloader({ isLoading, render, ...rest }) {
  return isLoading ? <SkeletonLoader {...rest} /> : render();
}

function Descriptor({ field, render, icon, ...rest }) {
  return (
    <Flex style={{ gap: 80 }} alignItems="center" marginBottom={16} {...rest}>
      <Flex alignItems="center">
        <Box marginRight={2}>{icon}</Box>
        <Typography
          color="textSecondary"
          fontSize={[14, 14]}
          lineHeight={1}
          fontWeight={500}
          mr={2}
          width="120px"
        >
          {field}:
        </Typography>
      </Flex>
      <Box flex="1">{render()}</Box>
    </Flex>
  );
}

/** =================== Styled components =================== */

const PageWrapper = styled(Box)`
  min-height: calc(100vh - 80px);
`;

const InnerCard = styled(Box)`
  overflow: hidden;
  margin: 0 auto;
`;

const HeaderRow = styled(Flex)`
  position: relative;
  align-items: flex-start;
`;

const LeftStripe = styled(Box)`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 8px;
  background: ${p => p.color || p.theme.colors.disabled};
  border-top-left-radius: 12px;
  border-bottom-left-radius: 12px;
`;

const HeaderContent = styled(Box)`
  width: 100%;
`;

const MetaList = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 6px;
`;

const MetaItem = styled(Box)`
  padding: 0;
`;

const CardSection = styled(Box)`
  background: ${p => p.theme.colors.white || '#fff'};
  border-radius: 10px;
  padding: 18px;
  box-shadow: none;
  margin-bottom: 16px;
  border: 1px solid ${p => p.theme.colors.greys[1] || '#f0f0f0'};
`;

const SectionHeader = styled(Box)`
  margin-bottom: 12px;
`;

const StyledTrans = styled(Trans)`
  font-weight: 500px;
`;

function getStatusColor(theme, status) {
  switch (status) {
    case 'upcoming':
      return theme.colors.danger || '#f0a';
    case 'completed':
      return theme.colors.success || '#28a745';
    case 'cancelled':
      return theme.colors.error || '#ff4d4f';
    default:
      return theme.colors.disabled || '#cfcfcf';
  }
}
